/* Kết quả tư vấn — thông tin khách, bác sĩ khám, tổng hợp; chốt (thanh toán / cọc) hoặc chưa chốt kèm lý do, ghi ngược về Telesales */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, DatePicker, Input, Modal, Radio, Tag, message } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import {
  RESULTS, activePlans, calc, custTitle, dmhm, doctorName, inExecRoom, paidOf, payRows, payStepText, quoteValid, reqLabel, stageOf, vnd,
  type FollowBy, type Result, type ResultKind, type Visit,
} from './logic';
import { useTuVan } from './store';
import { finishVisit } from './receptionApi';
import { pushQuoteToPos } from './pos';
import { Card, DoctorGate, FlowBar, Hint, NoVisit, NotStarted, Row, Shell, SourceTag, teleResult, useMe, useNow, useScreening, useVisit, who } from './shared';

/** Kết quả tư vấn = tổng hợp các bước: dịch vụ, bác sĩ khám, bệnh lý khai báo, tiền và kế hoạch thanh toán — lưu theo lượt vào hồ sơ khách */
function Summary({ v }: { v: Visit }) {
  const now = useNow();
  const { s, flags, loading, error } = useScreening(v);
  const q = v.quote;
  const total = q ? calc(q).total : 0;
  const paid = paidOf(v);
  const plan = payRows(v, now);
  return (
    <Card title="Tổng hợp tư vấn">
      <div className="space-y-2">
        <Row label="Dịch vụ">{q?.lines.length ? q.lines.map((l) => `${l.name} × ${l.qty}${l.gift ? ' (tặng)' : ''}`).join(' · ') : <span className="text-gray-400">Chưa có phiếu</span>}</Row>
        <Row label="Bệnh lý khai báo">{s ? (flags.length ? flags.join(' · ') : 'Không có') : <span className="text-gray-400">{loading ? 'Đang tải…' : error ?? 'Khách chưa khai báo'}</span>}</Row>
        <Row label="Tổng chi phí"><b>{vnd(total)}</b> · đã thu <b className="text-green-600">{vnd(paid)}</b> · còn lại <b className="text-[#3e91ab]">{vnd(Math.max(0, total - paid))}</b></Row>
        {plan.length > 0 && <Row label="Thanh toán">{plan.map((r) => `Lần ${r.no} ${vnd(r.amount)} (${payStepText(r)})`).join(' · ')}</Row>}
      </div>
      <Hint>Ghi kết quả là lưu bản tổng hợp này vào hồ sơ khách và ghi ngược về Telesales.</Hint>
    </Card>
  );
}

const CLOSED: ResultKind[] = ['pay', 'deposit'];
const OPEN: ResultKind[] = ['think', 'family', 'price', 'doctor', 'no_need', 'other'];

function Body({ v }: { v: Visit }) {
  const navigate = useNavigate();
  const me = useMe();
  const update = useTuVan((s) => s.update);
  const [edit, setEdit] = useState(!v.result);
  const [kind, setKind] = useState<ResultKind | undefined>(v.result?.kind);
  const [note, setNote] = useState(v.result?.note ?? '');
  const [cb, setCb] = useState<Dayjs | null>(v.result?.callbackAt ? dayjs(v.result.callbackAt) : dayjs().add(2, 'day').hour(10).minute(0));
  const [follow, setFollow] = useState<FollowBy>(v.result?.followBy ?? 'Telesales');
  const [saving, setSaving] = useState(false);
  const meta = kind ? RESULTS[kind] : undefined;
  const hasDep = v.deposits.some((d) => d.status !== 'cancelled') || (v.onlineDeps ?? []).some((d) => d.received);
  const qOk = quoteValid(v.quote);
  const total = v.quote ? calc(v.quote).total : 0;
  const started = v.status === 'consulting' || v.status === 'done';

  const block = !kind ? 'Chọn kết quả.'
    : kind === 'pay' && !qOk ? 'Chốt thanh toán cần phiếu báo giá chính thức có hiệu lực.'
      : kind === 'deposit' && !qOk ? 'Chốt cọc cần phiếu báo giá chính thức có hiệu lực.'
        : kind === 'deposit' && !hasDep ? 'Chưa có khoản thu — lập ở Kế hoạch thanh toán.'
          : meta?.callback && !cb ? 'Chọn ngày khách hẹn trả lời.'
            : (kind === 'other' || kind === 'family') && !note.trim() ? (kind === 'other' ? 'Ghi rõ lý do.' : 'Ghi người khách cần hỏi ý.')
              : undefined;

  const doSave = async () => {
    if (!kind || block) { message.warning(block); return; }
    const r: Result = {
      kind, note: note.trim(), at: Date.now(), by: me.name,
      amount: kind === 'pay' ? total : kind === 'deposit' ? paidOf(v) + v.deposits.filter((d) => d.status === 'requested').reduce((s, d) => s + d.amount, 0) : undefined,
      ...(RESULTS[kind].callback && cb ? { callbackAt: cb.valueOf() } : {}),
      ...(RESULTS[kind].follow ? { followBy: follow } : {}),
    };
    // Báo server kết thúc lượt trước: không báo được thì chưa lưu, vì lượt vẫn còn giữ phòng và tư vấn viên
    if (v.serverId && v.status === 'consulting') {
      setSaving(true);
      const fin = await finishVisit(v, 'done');
      setSaving(false);
      if (!fin.ok) { message.error(fin.msg); return; }
    }
    // Chốt thanh toán mà chưa chuyển POS: ghi giỏ lên lịch hẹn; không ghi được thì vẫn lưu kết quả và báo chuyển tay
    setSaving(true);
    const pos = kind === 'pay' && v.quote && !v.quote.posAt ? await pushQuoteToPos(v) : undefined;
    setSaving(false);
    const toPos = pos?.ok === true;
    update(v.id, (x) => ({ status: 'done', result: r, ...(toPos ? { quote: { ...x.quote!, posAt: Date.now() } } : {}) }),
      { text: `Ghi kết quả: ${RESULTS[kind].short} · mã ${stageOf(r)}${toPos ? ' · chuyển giỏ sang POS' : ''}`, by: me.name });
    teleResult(v, r);
    setEdit(false);
    message.success(`Đã lưu kết quả${v.leadId ? ' và cập nhật hồ sơ Telesales' : ''}${toPos ? ', giỏ đã ghi lên lịch hẹn cho quầy POS' : ''}.`);
    if (pos && !pos.ok) message.warning(`Chưa chuyển giỏ sang POS: ${pos.error}`);
  };

  // Server kết thúc lượt thì tự trả phòng thủ thuật và ghi xong dịch vụ, nên hỏi lại khi khách còn trong phòng
  const save = () => {
    if (v.status === 'consulting' && inExecRoom(v)) {
      Modal.confirm({
        title: `Khách đang làm dịch vụ ở ${v.exec!.room}`,
        content: 'Lưu kết quả sẽ kết thúc lượt: phòng được trả và dịch vụ ghi là đã xong. Nếu khách chưa làm xong, hãy lưu sau khi khách làm xong.',
        okText: 'Vẫn lưu', cancelText: 'Để sau', onOk: doSave,
      });
      return;
    }
    doSave();
  };

  const Opt = ({ k }: { k: ResultKind }) => <Radio value={k} className="!py-1">{RESULTS[k].label}</Radio>;

  if (v.result && !edit) {
    const r = v.result;
    return (
      <Card title={`Kết quả · ${v.cust.name}`} extra={<Tag color={RESULTS[r.kind].closed ? 'green' : 'orange'} className="!m-0">{RESULTS[r.kind].short} · {stageOf(r)}</Tag>}>
        <div className="space-y-1.5 text-[13px]">
          <div>Ghi lúc {dmhm(r.at)} · {r.by}</div>
          {r.amount != null && <div>Giá trị: <b>{vnd(r.amount)}</b></div>}
          {r.callbackAt && <div>Hẹn trả lời: <b>{dmhm(r.callbackAt)}</b></div>}
          {r.followBy && <div>Chăm tiếp: <b>{r.followBy}</b></div>}
          {r.note && <div>Ghi chú: {r.note}</div>}
          {v.leadId && <Hint>Đã ghi ngược kết quả về hồ sơ khách bên Telesales.</Hint>}
        </div>
        <div className="mt-4 flex flex-wrap gap-2 border-t border-gray-100 pt-3 dark:border-gray-700">
          {RESULTS[r.kind].closed && <Button type="primary" onClick={() => navigate(`/chuyen-thuc-hien?v=${v.id}`)}>Chuyển thực hiện</Button>}
          <Button onClick={() => setEdit(true)} disabled={activePlans(v).length > 0}>Sửa kết quả</Button>
          <Button onClick={() => navigate('/app-tuvan')}>Về Khách đang chờ</Button>
        </div>
        {activePlans(v).length > 0 && <Hint>Đã tạo lịch thực hiện — huỷ lịch trước khi sửa kết quả.</Hint>}
      </Card>
    );
  }

  return (
    <Card title={`Ghi kết quả · ${v.cust.name}`}>
      <Radio.Group value={kind} onChange={(e) => setKind(e.target.value)} disabled={!started} className="w-full">
        <div className="space-y-3">
          <div>
            <div className="mb-1 text-[13px] font-semibold text-gray-700 dark:text-gray-200">Chốt</div>
            <div className="flex flex-col pl-1">{CLOSED.map((k) => <Opt key={k} k={k} />)}</div>
          </div>
          <div className="border-t border-gray-100 pt-3 dark:border-gray-700">
            <div className="mb-1 text-[13px] font-semibold text-gray-700 dark:text-gray-200">Chưa chốt — chọn lý do</div>
            <div className="flex flex-col pl-1">{OPEN.map((k) => <Opt key={k} k={k} />)}</div>
          </div>
        </div>
      </Radio.Group>

      {meta && kind && (
        <div className="mt-3 space-y-3 rounded-r-lg border-l-4 border-[#3e91ab] bg-gray-50 p-3 text-[13px] dark:bg-gray-700/40">
          <div className="text-gray-700 dark:text-gray-200">{meta.hint}</div>
          {kind === 'pay' && v.quote && <div>Giỏ POS: <b>{v.quote.code}</b> · {vnd(total)}{paidOf(v) ? ` · trừ cọc ${vnd(paidOf(v))}` : ''}</div>}
          {kind === 'deposit' && !hasDep && <Link to={`/ke-hoach-thanh-toan?v=${v.id}`} className="text-[#3e91ab]">Lập kế hoạch thanh toán →</Link>}
          {(kind === 'pay' || kind === 'deposit') && !qOk && <Link to={`/phieu-bao-gia?v=${v.id}`} className="block text-[#3e91ab]">Hoàn tất phiếu báo giá →</Link>}
          {meta.callback && (
            <div className="flex flex-wrap items-center gap-2">Hẹn trả lời <DatePicker showTime={{ format: 'HH:mm', minuteStep: 15 }} format="HH:mm DD/MM/YYYY" value={cb} onChange={setCb} disabledDate={(d) => d.isBefore(dayjs(), 'day')} /></div>
          )}
          {meta.follow && (
            <div className="flex flex-wrap items-center gap-2">Ai chăm tiếp
              <Radio.Group value={follow} onChange={(e) => setFollow(e.target.value)} optionType="button" size="small" options={['Telesales', 'Tư vấn cơ sở']} />
            </div>
          )}
          <Input.TextArea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={kind === 'family' ? 'Người khách cần hỏi ý (VD: chồng, mẹ)' : 'Ghi chú (không ghi chẩn đoán)'} />
          <div className="text-xs text-gray-500">Mã S sau khi lưu: <b>{stageOf({ kind, followBy: follow })}</b>{v.leadId ? ' · cập nhật hồ sơ Telesales' : ''}</div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button type="primary" onClick={save} loading={saving} disabled={!started || !!block}>Lưu kết quả</Button>
        {v.result && <Button onClick={() => setEdit(false)}>Huỷ sửa</Button>}
        {block && kind && <span className="text-xs text-red-500">{block}</span>}
      </div>
    </Card>
  );
}

/** Thông tin cá nhân của khách: hồ sơ + phần khách tự khai (giới tính, tuổi, địa chỉ, nghề nghiệp) */
function Personal({ v }: { v: Visit }) {
  const sc = useScreening(v);
  const { s } = sc;
  return (
    <Card title="Thông tin cá nhân" extra={<SourceTag v={v} />}>
      <div className="space-y-2">
        <Row label="Họ tên"><b>{custTitle(v.cust)}</b></Row>
        <Row label="Mã KH · SĐT">{v.cust.code} · {v.cust.phone}</Row>
        {s && (s.gender != null || s.age || s.dob) && (
          <Row label="Giới tính · tuổi">{[s.gender === 1 ? 'Nam' : s.gender === 2 ? 'Nữ' : '', s.age ? `${s.age} tuổi` : '', s.dob ?? ''].filter(Boolean).join(' · ')}</Row>
        )}
        {s?.address && <Row label="Địa chỉ">{s.address}</Row>}
        {s?.job && <Row label="Nghề nghiệp">{s.job}{s.married != null ? ` · ${s.married ? 'Đã kết hôn' : 'Chưa kết hôn'}` : ''}</Row>}
        <Row label="Mảng · nhu cầu"><b>{v.mang}</b> · {v.need}</Row>
        <Row label="Tư vấn viên">{who(v.consultant) || '—'}{v.room ? ` · ${v.room}` : ''}</Row>
        {v.owner && <Row label={v.source === 'Telesales' ? 'Telesales' : 'Người phụ trách'}>{v.owner}</Row>}
        {!s && <Hint>{sc.loading ? 'Đang tải phiếu khai báo…' : sc.error ?? 'Khách chưa khai báo thông tin ban đầu.'}</Hint>}
      </div>
    </Card>
  );
}

/** Thông tin bác sĩ khám: bác sĩ, mức độ, vùng, dịch vụ chỉ định (không giá), yêu cầu chuyên môn, nhận xét, chữ ký */
function DoctorInfo({ v }: { v: Visit }) {
  const navigate = useNavigate();
  const q = v.quote;
  const e = q?.exam;
  return (
    <Card title="Thông tin bác sĩ khám" extra={e?.signedAt ? <Tag color="green" className="!m-0">Đã ký {dmhm(e.signedAt)}</Tag> : <Tag color="orange" className="!m-0">Chưa ký</Tag>}>
      {!q ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Hint>Bác sĩ chưa khám.</Hint>
          <Button size="small" onClick={() => navigate(`/bac-si-kham?v=${v.id}`)}>Mở bước Bác sĩ khám</Button>
        </div>
      ) : (
        // Chữ ký nằm cuối thẻ (thẻ giãn bằng cột bên phải nên đẩy xuống đáy, như chỗ ký cuối phiếu)
        <div className="flex flex-1 flex-col gap-3">
          <div className="space-y-2">
            <Row label="Bác sĩ">{e?.doctor ? doctorName(e.doctor) : '—'}</Row>
            <Row label="Mức độ · vùng">{[e?.level, e?.area].filter(Boolean).join(' · ') || '—'}</Row>
            <Row label="Dịch vụ chỉ định">{q.lines.length ? q.lines.map((l) => `${l.name} × ${l.qty}`).join(' · ') : '—'}</Row>
            {!!e?.reqs?.length && <Row label="Yêu cầu chuyên môn">{e.reqs.map((r) => <Tag key={r} color="red" className="!mb-1">{reqLabel(r)}</Tag>)}</Row>}
            {e?.note && <Row label="Nhận xét">{e.note}</Row>}
          </div>
          <div className="mt-auto border-t border-gray-100 pt-3 dark:border-gray-700">
            <Row label="Chữ ký bác sĩ">
              {e?.signedAt
                ? e.sign ? <img src={e.sign} alt={`Chữ ký ${doctorName(e.doctor)}`} className="h-20 rounded border border-gray-200 bg-white" /> : 'Đã ký xác nhận'
                : <Button size="small" type="link" className="!px-0" onClick={() => navigate(`/bac-si-kham?v=${v.id}`)}>Chưa ký — mở bước Bác sĩ khám</Button>}
            </Row>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function KetQuaTuVanPage() {
  const { v } = useVisit();
  return (
    <Shell title="Kết quả tư vấn" sub="Thông tin khách, bác sĩ khám, tổng hợp tư vấn · ghi chốt hoặc lý do chưa chốt">
      <FlowBar v={v} step="result" />
      {v ? (
        <DoctorGate v={v}>
          <NotStarted v={v} />
          <div className="grid grid-cols-1 gap-3 sm:gap-4 xl:grid-cols-2">
            <div className="flex flex-col gap-3 sm:gap-4 [&>*:last-child]:flex-1">
              <Personal v={v} />
              <DoctorInfo v={v} />
            </div>
            <div className="flex flex-col gap-3 sm:gap-4 [&>*:last-child]:flex-1">
              <Summary v={v} />
              <Body key={`${v.id}-${v.result?.at ?? 0}`} v={v} />
            </div>
          </div>
        </DoctorGate>
      ) : <NoVisit />}
    </Shell>
  );
}
